import {
  FulfillmentIssueTypeLabels,
  OpsSignalKind,
  TimeDecisionType,
  TireHotelIssueReason,
  TireSeasonLabels
} from '@navio/nsdk';
import { correctionRequestKindLabel } from '../correctionRequest/correctionRequestKindLabel';
import { CustomDateFormats, NoddiDateFormats, noddiFormatDate } from '../date/date';
import {
  FLEET_AUTO_BOOKING_SKIP_REASON_LABEL_BY_VALUE,
  SERVICE_WORKER_ABSENCE_TYPE_LABEL_BY_VALUE
} from '../options/optionTypes';
import { pluralizeWithValue } from '../string/string';
import { infeasibleRoutesSummary } from './infeasibleRouteCopy';
import { opsDuration } from './opsDuration';
import type { OpsSignal, OpsSignalOf } from './opsSignal';
import { wheelStorageIssueTitle } from './wheelStorageIssueCopy';

const day = (date: string): string => noddiFormatDate(date, CustomDateFormats.WEEKDAY_WITH_NUMBER_AND_MONTH);

const at = (dateTime: string): string => noddiFormatDate(dateTime, NoddiDateFormats.TIME);

/** Which catalog values the visit is still missing. */
export const missingWheelSpecLabel = (signal: OpsSignalOf<'MISSING_WHEEL_SPECS'>): string => {
  if (signal.missingTirePressure && signal.missingWheelTorque) {
    return 'Tire pressure and wheel nut torque';
  }
  return signal.missingTirePressure ? 'Tire pressure' : 'Wheel nut torque';
};

export type OpsSignalCopy = {
  summary: string;
  bannerTitle: string;
  body: string;
};

const WHEEL_SET_WITHOUT_LOCATION_COPY = {
  summary: 'Wheel set is at the facility but not on a storage unit.',
  body: 'The system shows this wheel set at the facility without a storage unit. Scan it onto one or assign it so picking and transfers stay reliable before the next booking or transfer.'
};

const NEEDS_NEW_LABEL_COPY = {
  summary: 'The label on a stored wheel set shows an old license plate.',
  body: 'The license plate changed after the label was printed. Print a new label with the new license plate, put it on the wheels, then use Confirm new label on the wheel set page.'
};

const WHEEL_SET_NOT_MOVED_COPY = {
  summary: 'A wheel set at the facility has not moved for longer than the chosen time.',
  body: 'The system says we hold this wheel set, but nothing has moved it for a long time. Find it on its storage unit and scan it there again in the facility app. If it is not there, correct its storage events so customers do not book against wheels we no longer hold.'
};

const WHEEL_STORAGE_COPY: Record<TireHotelIssueReason, { summary: string; body: string }> = {
  [TireHotelIssueReason.WHEEL_STORAGE_OWNER_NEEDS_REVIEW]: {
    summary: 'Stored wheels are registered on another owner than the tire hotel customer.',
    body: 'The car has several owners. On the issue, choose who should be the car’s only owner; the others are removed, and the tire hotel and stored wheels move to that owner.'
  },
  [TireHotelIssueReason.STORAGE_IS_MISSING]: {
    summary: 'Membership expects wheel sets that storage cannot find.',
    body: 'The tire hotel record says wheel sets should be in storage, but they are missing. Reconcile storage events, membership, and physical stock.'
  },
  [TireHotelIssueReason.STORAGE_IS_NOT_EXPECTED]: {
    summary: 'Wheel sets are in storage when membership or pickup status says they should not be.',
    body: 'Stored wheel sets do not match an active membership or expected pickup state. Return, dispose, or fix membership so records match the facility.'
  },
  [TireHotelIssueReason.MULTIPLE_WHEEL_SETS_IN_STORAGE]: {
    summary: 'More than one wheel set is stored for this customer.',
    body: 'Several wheel sets are stored for one customer. Verify which set belongs to which car or season and clean up duplicates.'
  },
  [TireHotelIssueReason.DUPLICATE_LICENSE_PLATE_ACROSS_USER_GROUPS]: {
    summary: 'More than one customer has stored wheel sets for this license plate.',
    body: 'Two or more customers have stored wheel sets tied to the same car. Resolve ownership before booking or pickup.'
  },
  [TireHotelIssueReason.CUSTOMER_PICKUP_STALE]: {
    summary: 'Picked-up wheel sets are not placed on a storage unit.',
    body: 'Wheel sets were picked up from the customer but are not on a storage unit. Scan them onto a storage unit or correct the storage events.'
  },
  [TireHotelIssueReason.MULTIPLE_SEASONS_IN_CUSTOMER_PICKUP]: {
    summary: 'More than one season was picked up for this customer.',
    body: 'Multiple seasons were picked up for this customer. Split or label sets so storage and bookings stay clear.'
  },
  [TireHotelIssueReason.WHEEL_SET_WITHOUT_LOCATION]: WHEEL_SET_WITHOUT_LOCATION_COPY,
  [TireHotelIssueReason.WHEEL_SET_NOT_MOVED]: WHEEL_SET_NOT_MOVED_COPY,
  [TireHotelIssueReason.NEEDS_NEW_LABEL]: NEEDS_NEW_LABEL_COPY
};

export const opsSignalCopy = (signal: OpsSignal, now: Date): OpsSignalCopy => {
  switch (signal.kind) {
    case OpsSignalKind.ROUTE_BEHIND_SCHEDULE:
      return signal.isStarted
        ? {
            summary: `Route is ${opsDuration(signal.lateBySeconds)} late with ${signal.remainingStops} stops left.`,
            bannerTitle: 'Route is running late',
            body: 'The worker started this route but is behind the plan. Open today’s routes to see remaining stops and delivery windows, and contact the worker if customers or later bookings are at risk.'
          }
        : {
            summary: `Route was planned to start at ${at(signal.plannedStartAt)} and has not started yet.`,
            bannerTitle: 'Route has not started',
            body: 'This route is still marked as not started after the planned start time. Check with the worker, confirm the route in the app, or adjust today’s plan so bookings on this route are not delayed.'
          };
    case OpsSignalKind.ROUTE_UNCONFIRMED:
      return {
        summary: `Worker has not confirmed this route for ${day(signal.date)}.`,
        bannerTitle: 'Route not confirmed by worker',
        body: 'The route is scheduled but the worker has not confirmed it in the app. Confirm they are working and ask them to confirm the route, or reassign work if they cannot run it.'
      };
    case OpsSignalKind.SOLUTION_NOT_SELECTED:
      return {
        summary:
          signal.numberOfCarsToPlan > 0
            ? `Not on a route yet: ${pluralizeWithValue({ value: signal.numberOfCarsToPlan, word: 'car' })} on ${day(signal.date)}.`
            : `No route solution is selected for ${day(signal.date)}.`,
        bannerTitle: 'No route solution selected',
        body: 'Route planning for this day has no selected solution, so confirmed bookings are not on a route. Open route planning, pick or rebuild a solution, and publish before the planning deadline so the day can be executed.'
      };
    case OpsSignalKind.ROUTE_PLANNER_FAILED:
      return {
        summary: `Route planning failed for ${day(signal.date)}.`,
        bannerTitle: 'Route planning failed',
        body: 'Automatic route planning failed for this day. Open route planning, review the error, fix bookings workers or areas if needed, and run planning again so bookings can be routed.'
      };
    case OpsSignalKind.INFEASIBLE_ROUTE_PLAN:
      return {
        summary: infeasibleRoutesSummary(signal.infeasibleRoutes),
        bannerTitle: 'No valid routes found',
        body: 'Planning found no valid routes for this day. Move bookings, or add or extend a shift. This clears when a run finds valid routes.'
      };
    case OpsSignalKind.FLEET_AUTO_BOOKING_BLOCKED:
      return {
        summary: `${pluralizeWithValue({ value: signal.cars.length, word: 'car' })} on ${signal.fleetScheduleName} could not be auto-booked for ${day(signal.date)}: ${FLEET_AUTO_BOOKING_SKIP_REASON_LABEL_BY_VALUE[signal.reason]}.`,
        bannerTitle: 'Fleet cars not auto-booked',
        body: 'The auto-booker could not book these cars for the date the fleet schedule needs them. Fix what stopped it — no free bookable time, no slot that day, or the schedule’s setup — or book the cars by hand; a booking clears this signal.'
      };
    case OpsSignalKind.UNABLE_TO_COMPLETE:
      return {
        summary: 'Worker marked work on this booking as unable to complete.',
        bannerTitle: 'Unable to complete',
        body: 'The worker reported that part or all of this booking could not be completed. Review what was marked on each task, decide follow-up (new booking, customer contact, ticket), then mark this signal handled, snooze it, or escalate.'
      };
    case OpsSignalKind.PAST_BOOKING_OPEN:
      return {
        summary: `Delivery window was ${day(signal.deliveryWindowStartsAt)} ${at(signal.deliveryWindowStartsAt)}; booking is still open.`,
        bannerTitle: 'Booking not completed',
        body: 'This booking’s delivery window is in the past but the booking is still confirmed or on a route and not completed. Open the booking, mark it completed or unable to complete, or reschedule if the work still needs to happen.'
      };
    case OpsSignalKind.NO_WORKER_SHIFT:
      return {
        summary: 'No worker shift covers the delivery window for this booking.',
        bannerTitle: 'No matching worker shift',
        body: 'The booking is confirmed, but no worker shift is scheduled for its delivery window. Add or extend a worker shift for that area and time, or reschedule the booking to a window that already has shift coverage.'
      };
    case OpsSignalKind.DEDICATED_WORKER_NOT_ON_SHIFT:
      return {
        summary: "None of the customer's dedicated workers has a shift that day.",
        bannerTitle: 'Dedicated worker not scheduled',
        body: "This customer has dedicated workers, but none of them has a shift on the booking's day. Route planning lets any worker take the booking. Add a shift for a dedicated worker if they should serve it, or leave it to any worker."
      };
    case OpsSignalKind.MOUNTING_OVERDUE:
      return {
        summary: `Tires received ${signal.receivedAt ? day(signal.receivedAt) : 'at the facility'}; mounting is overdue for an upcoming booking.`,
        bannerTitle: 'Tires waiting to be mounted',
        body: 'Tires for this order have been received at the facility but are not mounted yet, and a booking depends on them. Open the order, complete mounting, or adjust the booking if the timeline no longer works.'
      };
    case OpsSignalKind.TIRE_OFFER_NOT_CREATED:
      return {
        summary: `${TireSeasonLabels[signal.season]} tread on ${signal.licensePlate.number} was low, and no tire offer was drafted.`,
        bannerTitle: 'Tire offer not created',
        body: 'A completed booking measured tread at or below the recommended depth, and the system could not draft a tire offer. Open the booking and create the offer. A later offer for this car and season clears the signal.'
      };
    case OpsSignalKind.TIRE_ORDER_FAILED:
      return {
        summary: signal.message,
        bannerTitle: FulfillmentIssueTypeLabels[signal.issueType],
        body: 'The supplier order did not go through. Open the tire offer, fix the cause, and place it again. The signal clears when the failure is resolved or cancelled.'
      };
    case OpsSignalKind.MISSING_WHEEL_SPECS:
      return {
        summary: `${signal.licensePlate.number} is booked ${day(signal.deliveryWindowStartsAt)} and is missing ${missingWheelSpecLabel(signal).toLowerCase()}.`,
        bannerTitle: 'Missing tire pressure or torque',
        body: 'Workers need tire pressure and wheel nut torque before this visit. Open the booking, then fill the missing catalog values on the car. The signal clears when both values exist.'
      };
    case OpsSignalKind.WHEEL_SET_WITHOUT_LOCATION:
      return { ...WHEEL_SET_WITHOUT_LOCATION_COPY, bannerTitle: 'Wheel set not on a storage unit' };
    case OpsSignalKind.WHEEL_SET_NOT_MOVED:
      return {
        summary: `Not moved since ${noddiFormatDate(signal.lastMovedAt, NoddiDateFormats.DATE)}${
          signal.storageFacility
            ? ` at ${signal.storageFacility.name}${signal.storageUnitName ? `, unit ${signal.storageUnitName}` : ''}`
            : ''
        }.`,
        bannerTitle: 'Wheel set not moved',
        body: WHEEL_SET_NOT_MOVED_COPY.body
      };
    case OpsSignalKind.WHEEL_STORAGE_ISSUE: {
      const issue = WHEEL_STORAGE_COPY[signal.issue.reason];
      return {
        summary: issue.summary,
        bannerTitle: wheelStorageIssueTitle(signal.issue),
        body: issue.body
      };
    }
    case OpsSignalKind.TRANSFER_ORDER_NEEDED:
      return {
        summary: `${pluralizeWithValue({ value: signal.wheelSetCount, word: 'wheel set' })} must move before bookings on ${day(signal.bookingDate)}.`,
        bannerTitle: 'Transfer order needed',
        body: 'Bookings on this date need wheel sets at the receiving facility, but those sets are still at the source facility. Create a transfer order so wheels arrive before the booking delivery windows.'
      };
    case OpsSignalKind.TRANSFER_ORDER_STUCK:
      return {
        summary: `Transfer delivery is due ${day(signal.transferOrder.scheduledDeliveryDate)}; progress is blocked or overdue.`,
        bannerTitle: 'Transfer order has a problem',
        body: 'A transfer order exists but progress stalled: some wheel sets could not be moved, the source reported a problem, and/or delivery is late. Open the transfer order, fix blocking issues, and complete the move before affected bookings.'
      };
    case OpsSignalKind.ABSENCE_REQUEST: {
      const start: Date = new Date(signal.absence.startDate);
      return {
        summary:
          start > now
            ? `${signal.absence.serviceWorker.name} · absence starts ${day(signal.absence.startDate)} · awaiting approval.`
            : `${signal.absence.serviceWorker.name} · absence awaiting approval.`,
        bannerTitle: `Absence request · ${SERVICE_WORKER_ABSENCE_TYPE_LABEL_BY_VALUE[signal.absence.absenceType]}`,
        body: 'A worker submitted an absence that is not approved yet. Approve or decline so routes and worker shifts can be replanned for these dates.'
      };
    }
    case OpsSignalKind.OVERTIME_OR_CORRECTION: {
      const { decision } = signal;
      if (decision.type === TimeDecisionType.OVERTIME) {
        return {
          summary: `${decision.overtimeEntry.serviceWorker.name} · overtime on ${day(decision.overtimeEntry.startsAt)}.`,
          bannerTitle: 'Overtime request',
          body: 'The worker logged overtime that still needs a decision. Approve to include it in payroll, or decline with a note if the hours should not be paid.'
        };
      }
      return {
        summary: `${decision.correctionRequest.serviceWorker.name} asked to correct clocked time for ${day(decision.correctionRequest.observedClockInAt)}.`,
        bannerTitle: 'Shift correction request',
        body: `The worker asked to correct ${correctionRequestKindLabel[decision.correctionRequest.kind]} for this shift. Review the proposed change and worker reason, then approve or decline so payroll and shift records stay correct.`
      };
    }
  }
};

/** Sheet header when the row title should not repeat as the sheet title. */
export const opsSignalSheetHeader = (signal: OpsSignal): { title: string; description?: string } | null => {
  switch (signal.kind) {
    case OpsSignalKind.NO_WORKER_SHIFT:
    case OpsSignalKind.DEDICATED_WORKER_NOT_ON_SHIFT:
    case OpsSignalKind.PAST_BOOKING_OPEN:
      return {
        title: signal.userGroup.name,
        description: `${day(signal.deliveryWindowStartsAt)} ${at(signal.deliveryWindowStartsAt)}`
      };
    case OpsSignalKind.MOUNTING_OVERDUE: {
      const name = signal.inventoryOrder.customerName;
      const plate = signal.inventoryOrder.licensePlateNumber;
      return {
        title: name ?? plate ?? 'Mounting overdue',
        description: plate && name ? plate : undefined
      };
    }
    case OpsSignalKind.TIRE_OFFER_NOT_CREATED:
    case OpsSignalKind.MISSING_WHEEL_SPECS:
      return {
        title: signal.userGroup.name,
        description: signal.licensePlate.number
      };
    case OpsSignalKind.TIRE_ORDER_FAILED:
      return {
        title: signal.licensePlate.number,
        description: FulfillmentIssueTypeLabels[signal.issueType]
      };
    case OpsSignalKind.WHEEL_SET_WITHOUT_LOCATION:
    case OpsSignalKind.WHEEL_SET_NOT_MOVED:
      return { title: signal.wheelSet.owner.name };
    case OpsSignalKind.WHEEL_STORAGE_ISSUE:
      return {
        title: signal.issue.userGroup.name,
        description: wheelStorageIssueTitle(signal.issue)
      };
    case OpsSignalKind.TRANSFER_ORDER_NEEDED:
      return {
        title: `Bookings on ${day(signal.bookingDate)}`,
        description: `${signal.sourceStorageFacility.name} → ${signal.receivingStorageFacility.name}`
      };
    case OpsSignalKind.TRANSFER_ORDER_STUCK:
      return {
        title: `Transfer order #${signal.transferOrder.id}`,
        description: `${signal.transferOrder.sourceStorageFacility.name} → ${signal.transferOrder.destinationStorageFacility.name}`
      };
    case OpsSignalKind.ROUTE_BEHIND_SCHEDULE:
      return {
        title: signal.worker?.name ?? `Route ${signal.routeId}`,
        description: signal.isStarted
          ? `${opsDuration(signal.lateBySeconds)} late · ${signal.remainingStops} stops left`
          : `Planned start ${at(signal.plannedStartAt)}`
      };
    case OpsSignalKind.ROUTE_UNCONFIRMED:
      return {
        title: signal.worker?.name ?? `Route ${signal.routeId}`,
        description: `Scheduled ${day(signal.date)} ${at(signal.plannedStartAt)}`
      };
    default:
      return null;
  }
};
